import { useState, useMemo } from 'react';
import { TextField, InputAdornment, IconButton, Box, Menu, MenuItem } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClearIcon from '@mui/icons-material/Clear';
import SearchBar from 'components/SearchBar';
import { ActivityActionType, ActivityActionTypeLabels } from '../types/activityLog';

interface ActionTypeSelectorProps {
  selectedActionType: string;
  onSelectionChange: (actionType: string) => void;
}

export const ActionTypeSelector: React.FC<ActionTypeSelectorProps> = ({
  selectedActionType,
  onSelectionChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const open = Boolean(anchorEl);

  // All available action types
  const allActionTypes = useMemo(
    () =>
      Object.entries(ActivityActionTypeLabels).map(([value, label]) => ({
        value,
        label,
      })),
    [],
  );

  // Filtered action types based on search
  const filteredActionTypes = useMemo(() => {
    if (!searchQuery) return allActionTypes;
    const query = searchQuery.toLowerCase();
    return allActionTypes.filter(
      (item) =>
        item.label.toLowerCase().includes(query) || item.value.toLowerCase().includes(query),
    );
  }, [allActionTypes, searchQuery]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery('');
  };

  const handleSelectActionType = (actionType: string) => {
    onSelectionChange(actionType);
    handleClose();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectionChange('');
  };

  const selectedLabel = useMemo(
    () =>
      selectedActionType
        ? ActivityActionTypeLabels[selectedActionType as ActivityActionType] || selectedActionType
        : '',
    [selectedActionType],
  );

  const anchorWidth = anchorEl?.getBoundingClientRect
    ? anchorEl.getBoundingClientRect().width
    : undefined;

  return (
    <>
      <TextField
        variant="outlined"
        label="Action Type"
        value={selectedLabel}
        placeholder="Select action type"
        onClick={handleClick}
        fullWidth
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {selectedActionType && (
                    <IconButton
                      size="small"
                      onClick={handleClear}
                      sx={{ mr: -0.5 }}
                      aria-label="Clear action type selection"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  <ArrowDropDownIcon />
                </Box>
              </InputAdornment>
            ),
          },
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableAutoFocusItem
        slotProps={{
          paper: {
            style: {
              transform: 'translateX(0px) translateY(0px)',
              maxHeight: 316,
              minWidth: anchorWidth,
            },
          },
          list: {
            style: {
              border: '1px solid rgba(0,0,0,.1)',
              borderRadius: '5px',
            },
          },
        }}
      >
        <div>
          <SearchBar inputValue={searchQuery} setInputValue={setSearchQuery} />
          {filteredActionTypes.map((item) => (
            <MenuItem key={item.value} onClick={() => handleSelectActionType(item.value)}>
              {item.label}
            </MenuItem>
          ))}
        </div>
      </Menu>
    </>
  );
};
